import { useContext } from 'react';
import type { ReactNode } from 'react';
import { makeStyles } from '@mui/styles';
import { STATUSES } from '@openclassrooms/ui-web/learning-activities';
import cx from 'classnames';
import CourseContext from '../../contexts/courseContext';
import CourseProgress from '../CourseProgress';
import LearningOutcome from '../LearningOutcome';
import ProgressionPicture from './PictureProgression';

export interface CourseFinishedCardProps {
  icon: ReactNode;
  title: ReactNode;
  startBlock: ReactNode;
  endBlock?: ReactNode;
}

const CARD_SIZE = 288;
const useStyles = makeStyles(
  ({ palette, shape, spacing, breakpoints }) => ({
    wrapper: {
      width: CARD_SIZE,
      [breakpoints.up('sm')]: {
        width: 'revert',
        display: 'inline-block',
        minWidth: CARD_SIZE,
      },
    },
    cardTitle: {
      textAlign: 'center',
      color: palette.success.main,
    },
    card: {
      display: 'flex',
      flexDirection: 'row',
      border: `1px solid ${palette.stroke}`,
      background: palette.background.paper,
      borderRadius: shape.borderRadius,
    },
    col: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: spacing(3, 2),
      [breakpoints.up('sm')]: {
        padding: spacing(4),
      },
    },
    startBlock: {
      paddingBottom: spacing(2),
    },
    endBlock: {
      paddingTop: spacing(2),
      borderTop: `1px solid ${palette.stroke}`,
    },
    imageWrapper: {
      alignSelf: 'flex-end',
      marginRight: spacing(-1),
      marginBottom: spacing(-3),
      [breakpoints.up('sm')]: {
        marginRight: spacing(-3),
      },
    },
    courseProgressWrapper: {
      alignItems: 'center',
    },
  }),
  { classNamePrefix: 'CourseInProgressCard' }
);

function CourseInProgressCard() {
  const classes = useStyles();
  const { course, learningOutcomes, isLoadingLearningOutcomes } = useContext(CourseContext);

  const passedOutcomes = (learningOutcomes || []).filter(
    (outcome: { status: string }) => outcome.status === STATUSES.STATUS_PASSED
  );

  const progression = course.userCourseCompletionRate || 0;
  const showLearningOutcomes =
    isLoadingLearningOutcomes || (!isLoadingLearningOutcomes && learningOutcomes?.length > 0);

  return (
    <div className={classes.wrapper} data-testid="course-in-progress-card">
      <div className={classes.card}>
        <div className={classes.col}>
          <div
            className={cx(classes.startBlock, {
              [classes.courseProgressWrapper]: !isLoadingLearningOutcomes && learningOutcomes,
            })}
          >
            <CourseProgress progression={progression} />
          </div>

          {showLearningOutcomes && (
            <div className={classes.endBlock}>
              <LearningOutcome
                value={passedOutcomes.length}
                max={learningOutcomes?.length || 100}
                isLoading={isLoadingLearningOutcomes}
              />
            </div>
          )}
        </div>

        <div className={classes.imageWrapper}>
          <ProgressionPicture progression={progression} />
        </div>
      </div>
    </div>
  );
}

export default CourseInProgressCard;
